/**
 * Klaviyo Client Subscription API — subscribes profiles to a list with email/SMS consent.
 * _learnq does NOT support list subscription; we must call the REST API.
 *
 * Public API key: set window.__KLAVIYO_PUBLIC_KEY__, or KLAVIYO_PUBLIC_KEY below, or we try to
 * read it from the Klaviyo script tag (static.klaviyo.com/onsite/js/XXXXX/...).
 */
const KLAVIYO_PUBLIC_KEY = 'WVXHYb'; // 6-char Site ID from Klaviyo Settings > API Keys
const KLAVIYO_LIST_ID = 'RBBSVY'; // Email master list (Shopify-Klaviyo integration newsletter list)
const KLAVIYO_SMS_LIST_ID = 'RBijDB'; // Dedicated SMS list
const KLAVIYO_CLIENT_SUBSCRIBE_BASE = 'https://a.klaviyo.com/client/subscriptions';

function getPublicKey(): string {
  if (typeof window === 'undefined') return '';
  const w = window as any;
  if (w.__KLAVIYO_PUBLIC_KEY__) return w.__KLAVIYO_PUBLIC_KEY__;
  if (KLAVIYO_PUBLIC_KEY) return KLAVIYO_PUBLIC_KEY;
  // Try to read from Klaviyo script tag: .../onsite/js/XXXXX/klaviyo.js
  const script = document.querySelector('script[src*="klaviyo.com"]') as HTMLScriptElement | null;
  if (script?.src) {
    const m = script.src.match(/klaviyo\.com\/onsite\/js\/([^/]+)/);
    if (m?.[1]) return m[1];
  }
  return '';
}

function toE164(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return `+${digits}`;
}

export interface KlaviyoSubscribeOptions {
  email?: string;
  phone?: string;
  smsConsent?: boolean;
  listId?: string;
}

/**
 * Subscribe a profile to a Klaviyo list via the Client Subscription API.
 *
 * The API has no explicit channel field: attaching `email` to a list grants email marketing
 * consent on THAT list, and attaching `phone_number` grants SMS marketing consent on THAT list.
 * So email and phone must never be sent together in one call when they belong on different lists
 * (e.g. email -> master list, phone -> dedicated SMS list) — bundling them opts the phone number
 * into SMS marketing on whichever single list the call targets. Defaults to the email master list;
 * pass listId to target the SMS list instead. Transactional requires server-side Subscribe Profiles API.
 */
export async function subscribeToKlaviyoList(options: KlaviyoSubscribeOptions): Promise<void> {
  const { email: rawEmail, phone, smsConsent = false, listId = KLAVIYO_LIST_ID } = options;

  const email = rawEmail?.trim();

  const publicKey = getPublicKey();
  if (!publicKey) return;

  const profileAttrs: Record<string, unknown> = {};
  if (email) profileAttrs.email = email;
  // Only attach a phone number when SMS consent was actually given — this check is the only
  // thing standing between "gave us a phone number" and "consented to SMS marketing".
  if (smsConsent && phone?.trim()) {
    profileAttrs.phone_number = toE164(phone.trim());
  }

  // Need at least one identifying/consent attribute to subscribe.
  if (!profileAttrs.email && !profileAttrs.phone_number) return;

  // Client Subscription API only accepts profile (email, phone_number); no channels/subscriptions field. Defaults to MARKETING consent.
  const body = {
    data: {
      type: 'subscription',
      attributes: {
        custom_source: 'account_registration',
        profile: { data: { type: 'profile', attributes: profileAttrs } },
      },
      relationships: { list: { data: { type: 'list', id: listId } } },
    },
  };

  // Client Subscription API requires company_id (public key) in the URL; 403 "Invalid company" if missing
  const url = `${KLAVIYO_CLIENT_SUBSCRIBE_BASE}?company_id=${encodeURIComponent(publicKey)}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      revision: '2024-07-15',
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Klaviyo subscribe ${res.status}: ${text}`);
  }
}

declare global {
  interface Window {
    _learnq: any[];
  }
}

export interface KlaviyoAccountCreationData {
  email?: string;
}

export async function identifyOnAccountCreation(accountData: KlaviyoAccountCreationData): Promise<void> {
  const email = accountData?.email?.trim();
  if (!email) return;

  try {
    if (typeof window !== 'undefined' && window._learnq) {
      window._learnq.push(['identify', {
        $email: email,
        marketing_consent: true,
        consent_date: new Date().toISOString(),
        consent_method: 'account_registration',
      }]);
      window._learnq.push(['track', 'Account Created', { $email: email, marketing_opt_in: true }]);
    }
  } catch(error: any) {
    console.error(error);
  }
}

export interface KlaviyoEnrichData {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  sms_marketing_accept?: boolean | string;
}

export interface KlaviyoStartedQuestionnaireData {
  email?: string;
  productUrl?: string;
  productName?: string;
}

export async function trackStartedQuestionnaire(data: KlaviyoStartedQuestionnaireData): Promise<void> {
  const email = data?.email?.trim();
  if (!email) return;

  try {
    if (typeof window !== 'undefined' && window._learnq) {
      window._learnq.push(['identify', { $email: email }]);
      window._learnq.push(['track', 'Started Questionnaire', {
        $email: email,
        product_url: data.productUrl,
        product_name: data.productName,
        date: new Date().toISOString(),
      }]);
    }
  } catch (error: any){
    console.error(error);
  }
}

export async function enrichKlaviyoProfile(accountData: KlaviyoEnrichData): Promise<void> {
  if (!accountData?.email || !accountData?.firstName || !accountData?.lastName) return;

  try {
    const phoneDigits = accountData.phone?.replace(/\D/g, '');
    const smsConsent =
      accountData.sms_marketing_accept === true || accountData.sms_marketing_accept === 'true';

    // Re-confirm the email subscription on the master list (idempotent — already subscribed at
    // email-capture step).
    await subscribeToKlaviyoList({ email: accountData.email });

    // Phone is a separate channel on a separate list — subscribed on its own, never bundled with
    // email in the same call, so it lands on the dedicated SMS list instead of the email list.
    if (smsConsent && phoneDigits) {
      await subscribeToKlaviyoList({
        phone: phoneDigits,
        smsConsent: true,
        listId: KLAVIYO_SMS_LIST_ID,
      });
    }

    const formattedPhone = phoneDigits ? toE164(phoneDigits) : undefined;

    if (typeof window !== 'undefined' && window._learnq) {
      window._learnq.push(['identify', {
        $email: accountData.email,
        $first_name: accountData.firstName,
        $last_name: accountData.lastName,
        ...(formattedPhone && smsConsent && { $phone_number: formattedPhone }),
        ...(smsConsent && formattedPhone && { sms_marketing_consent: true, sms_consent_date: new Date().toISOString() }),
      }]);
    }
  } catch (err){
    console.error(err);
  }
}
